import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createPageContainer } from "../../components/ui/page-container";
import { createSkipLink } from "../../components/ui/skip-link";
import { fetchQuizAssignmentTranscript } from "../../lib/api-quizzes";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";
import {
  isTranscriptPayload,
  renderTranscriptContent,
} from "./transcript-shared";

export interface RenderOrganizerQuizTranscriptPageOptions {
  signal: AbortSignal;
  locale: SupportedLocale;
  quizId: string;
  assignmentId: string;
}

/**
 * Full-page organizer transcript (**GET** **`.../assignments/:id/transcript`**).
 */
export async function renderOrganizerQuizTranscriptPage(
  mount: HTMLElement,
  t: Translate,
  options: RenderOrganizerQuizTranscriptPageOptions,
): Promise<void> {
  mount.replaceChildren();
  mount.className = "";

  const shell = document.createElement("div");
  shell.className = "home-shell";
  mount.appendChild(shell);

  shell.appendChild(
    createSkipLink(t("quiz.home.skip_to_main"), "quiz-transcript-main"),
  );

  const page = createPageContainer();
  shell.appendChild(page);

  const { header, authSlot } = createStandardSiteAppHeader(t, options.locale);
  page.appendChild(header);

  void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
    signal: options.signal,
  });

  const mainEl = document.createElement("main");
  mainEl.id = "quiz-transcript-main";
  mainEl.className = "quiz-transcript-page__main";
  mainEl.tabIndex = -1;
  page.appendChild(mainEl);

  const nav = document.createElement("p");
  nav.className = "quiz-transcript-page__nav";
  const back = document.createElement("a");
  back.href = `/quizzes/view/${encodeURIComponent(options.quizId)}`;
  back.textContent = t("quiz.detail.transcript_back_to_quiz");
  nav.appendChild(back);
  mainEl.appendChild(nav);

  const h1 = document.createElement("h1");
  h1.className = "quiz-transcript-page__title";
  h1.textContent = t("quiz.detail.transcript_title");
  mainEl.appendChild(h1);

  const status = document.createElement("p");
  status.className = "quiz-transcript-page__status";
  status.textContent = t("quiz.detail.transcript_loading");
  mainEl.appendChild(status);

  const content = document.createElement("div");
  content.className = "quiz-transcript-page__content";
  mainEl.appendChild(content);

  try {
    const res = await fetchQuizAssignmentTranscript(
      options.quizId,
      options.assignmentId,
      { signal: options.signal },
    );
    if (options.signal.aborted) {
      return;
    }
    if (res.status === 401) {
      status.textContent = t("quiz.detail.sign_in_required");
      const p = document.createElement("p");
      const a = document.createElement("a");
      a.href = "/organizer/sign-in";
      a.textContent = t("quiz.organizer.sign_in.home_link");
      p.appendChild(a);
      mainEl.appendChild(p);
      return;
    }
    if (!res.ok) {
      status.textContent = t("quiz.detail.transcript_error");
      return;
    }
    let raw: unknown;
    try {
      raw = await res.json();
    } catch {
      status.textContent = t("quiz.detail.transcript_error");
      return;
    }
    if (!isTranscriptPayload(raw)) {
      status.textContent = t("quiz.detail.transcript_error");
      return;
    }
    status.textContent = "";
    renderTranscriptContent(
      content,
      t,
      options.locale,
      raw,
      raw.recipient_email,
    );
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    status.textContent = t("quiz.detail.transcript_error");
  }
}
